
import Image from 'next/image';

import { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';

import styles from './styles.module.scss';

import errorMsg from '../../Functions/errorMsg';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  DndContext,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  MouseSensor,
  TouchSensor
} from '@dnd-kit/core';
import {  
  SortableContext,
  arrayMove
} from '@dnd-kit/sortable';

import Resizer from 'react-image-file-resizer'
import { PuffLoader } from 'react-spinners';

import clarifaiApp from '../../../mongodb/clarifaiClient'
import axios from 'axios';

export default function GridDragAndDrop({ initialImages, pendingRequest, receiveUploadData }) {

  const [isUploading, setIsUploading] = useState(false);
  const [uploads, setUploads] = useState(initialImages === null ? [] : initialImages);
  
  useEffect(() => {
    receiveUploadData(uploads);
  }, [uploads])

  async function uploadFileMethods(e) {
    e.preventDefault();
    if (isUploading) return;
    if (pendingRequest) return;
    if (typeof window === 'undefined') return;
  
    const heic2any = require('heic2any');
  
    const files = e.target.files || e.dataTransfer.files;
  
    if (uploads.length === 12) {
      errorMsg("Cannot upload more images, 12 images are the max.");
      return;
    }
  
    if (files.length > 12) {
      errorMsg("You can only upload a maximum of 12 images.");
      return;
    }
  
    setIsUploading(true);
  
    var addedImages = [];
  
    // Use map to create an array of promises
    const uploadPromises = Array.from(files).map(async (image, index) => {
      var fileSize = (image.size / (1024 * 1024)).toFixed(2);
      if (fileSize > 8) {
        errorMsg("Image is too large, images can be no bigger than 8mb");
        return;
      } else {
        let resizedImage;
        // Check if the file is HEIC
        if (image.name.includes('.HEIC')) {
          // Convert HEIC to JPEG
          const jpegBlob = await heic2any({ blob: image, toType: 'image/jpeg' });
          // Resize the converted JPEG image
          resizedImage = await resizeFile(jpegBlob);
        } else {
          // Resize non-HEIC images directly
          resizedImage = await resizeFile(image);
        }
  
        // Create a FileReader instance
        const reader = new FileReader();
  
        // Return a new promise that resolves when the FileReader finishes
        return new Promise((resolve, reject) => {
          reader.onloadend = async () => {
            try {
              // Ensure the result is a string
              if (typeof reader.result !== 'string') {
                console.error('Invalid data URL:', reader.result);
                reject('Invalid data URL');
                return;
              }
  
              // Extract the base64 data from the data URL
              const base64Data = reader.result.split(',')[1];
  
              let getConcepts = [];
              try {
                // Analyze image with Clarifai
                const clarifaiResponse = await axios.post(
                  `${window.location.origin}/api/proxy/clarifai`,
                  {
                    inputs: [
                      {
                        data: {
                          image: {
                            base64: base64Data, // Send the base64 image data
                          },
                        },
                      },
                    ],
                  }
                );
              
                // Extract concepts
                getConcepts = clarifaiResponse?.data?.outputs?.[0]?.data?.concepts || [];
              } catch (error) {
                console.error("Error analyzing image with Clarifai:", error);
                throw new Error("Failed to analyze image with Clarifai.");
              }
  
              const thresholds = {
                nude: 0.9,
                gun: 0.9,
                bomb: 0.9,
                gore: 0.9,
                danger: 0.9,
              };
  
              const filteredConcepts = getConcepts.filter(concept => {
                return (
                  (concept.name === 'nude' && concept.value >= thresholds.nude) ||
                  (concept.name === 'gun' && concept.value >= thresholds.gun) ||
                  (concept.name === 'bomb' && concept.value >= thresholds.bomb) ||
                  (concept.name === 'gore' && concept.value >= thresholds.gore) ||
                  (concept.name === 'danger' && concept.value >= thresholds.danger)
                );
              });

              console.log(filteredConcepts)
  
              if (filteredConcepts.length > 0) {
                errorMsg("The images you're attempting to upload are against our policies.");
                reject("Image violates policy");
              } else {
                addedImages.push({
                  position: index + (uploads.length + 1),
                  url: URL.createObjectURL(resizedImage),
                  imageFile: image,
                });
                resolve();
              }
            } catch (error) {
              reject(error);
            }
          };
  
          // Read the resized image Blob as a data URL
          reader.readAsDataURL(resizedImage);
        });
      }
    });
  
    try {
      // Wait for all promises to complete
      await Promise.all(uploadPromises);
  
      const addToUploads = uploads.concat(addedImages);
      console.log(addToUploads);
  
      setUploads(addToUploads);
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  }
  
  
  
  
  const resizeFile = (file) =>
    new Promise((resolve, reject) => {
      Resizer.imageFileResizer(
        file,
        750,
        750,
        "PNG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "blob",
        300,
        0
      );
    });

  const removeImage = (index) => {
    if (pendingRequest === true) return;
    if (index < -1) {
      alert("There was an error deleting the image, please refresh the page.");
      return;
    }

    let newUploads = uploads.filter((image) => image.position !== index);

    setUploads(newUploads);
    receiveUploadData(newUploads);
  };

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const touchSensor = useSensor(TouchSensor); // Add TouchSensor

  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor); // Include touchSensor

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) {
      return;
    }
  
    if (active.id !== over.id) {
      setUploads((images) => {
        const oldIndex = images.findIndex((image) => image.position === active.id);
        const newIndex = images.findIndex((image) => image.position === over.id);
  
        if (oldIndex === -1 || newIndex === -1) {
          return images;
        }
  
        return arrayMove(images, oldIndex, newIndex);
      });
    }
  }

  return (
    <div id={styles.gridWrapper}>
      <DndContext id={styles.gridContext} onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
        <div id={styles.uploadBlock} style={{display: uploads.length === 12 ? 'none' : 'flex'}}>
            { !isUploading ? <FontAwesomeIcon icon={faUpload} id={styles.icon} /> : <PuffLoader size={48} color='#818181' /> }
            <input disabled={pendingRequest} type='file' accept='image/*' onChange={uploadFileMethods} multiple />
        </div>
        <SortableContext id={styles.gridSortContext} items={uploads.map((i) => i?.position)}>
          {uploads.map((image) => {
            return (
              <SortableItem handle={true} key={image.position} id={image.position} image={image} removeImage={removeImage} />
            )
          })}
        </SortableContext>
      </DndContext>
    </div>
  )
}

function SortableItem({ id, image, removeImage}) {

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({id: id}); 

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleRemoveImage = (event) => {
    event.stopPropagation();
    removeImage(image.position);
  };
  
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={styles.gridBox}>
      <FontAwesomeIcon icon={faXmarkCircle} id={styles.delete} onClick={handleRemoveImage} onTouchEnd={handleRemoveImage}  />
      <Image sizes='auto auto' fill src={image.url} alt={image.url} />
    </div>
  );
}